/**
 * Azure Cloud Client for SMS Pro
 *
 * CRITICAL USAGE RESTRICTION:
 * This client MUST ONLY be accessed through UniversalSyncBridge.
 * Direct usage will bypass array wrapping and corrupt array data.
 *
 * UniversalSyncBridge handles:
 * - Array wrapping: { items: [...], _isArray: true }
 * - Corrupted array recovery
 * - Event-driven sync (sr:storage:set, sr:sync:hydrate)
 *
 * This client provides the low-level push/pull/subscribe interface
 * but does NOT handle data transformation.
 *
 * ---
 *
 * Replacement for supabaseCloudClient.js - uses Azure PostgreSQL API instead of Supabase
 * Matches the interface expected by UniversalSyncBridge
 *
 * Multi-Tenancy: Data is scoped by organization_id at the backend API level
 * Backend enforces:
 * - Organization ownership verification (JWT organizationId)
 * - Vessel access control (user must have access to vessel)
 * - Permission levels (full vs view_only)
 *
 * Migration from Supabase:
 * - Removed: Supabase real-time subscriptions (unreliable)
 * - Added: Explicit push/pull sync with Azure API
 * - Security: JWT tokens instead of Supabase RLS
 */

import { azureApiClient } from '../lib/azureApiClient';
import { devLog, devError } from '../utils/devLog';

/**
 * Cache for organization ID to avoid repeated queries
 */
let cachedOrganizationId = null;
let cachedVesselId = null;

/**
 * Get the organization ID for the current user
 * @returns {string|null} Organization ID or null
 */
function getUserOrganizationId() {
  if (cachedOrganizationId) {
    return cachedOrganizationId;
  }

  cachedOrganizationId = azureApiClient.getOrganizationId();
  devLog('[SYNC] Organization ID cached:', cachedOrganizationId);
  return cachedOrganizationId;
}

/**
 * Get the current vessel ID from localStorage or default
 * @returns {string}
 */
function getCurrentVesselId() {
  if (cachedVesselId) {
    return cachedVesselId;
  }

  // Try to get from localStorage (set by vessel selector)
  const storedVesselId = localStorage.getItem('selected_vessel_id');
  if (storedVesselId) {
    cachedVesselId = storedVesselId;
    return cachedVesselId;
  }

  // Fall back to first vessel in user's vessels
  const vessels = azureApiClient.getUserVessels();
  if (vessels && vessels.length > 0) {
    cachedVesselId = vessels[0];
    return cachedVesselId;
  }

  // Ultimate fallback
  return 'default';
}

/**
 * Clear cached IDs (call on logout or vessel change)
 */
export function clearSyncCache() {
  cachedOrganizationId = null;
  cachedVesselId = null;
  devLog('[SYNC] Sync cache cleared');
}

/**
 * Azure implementation of cloud client
 * Matches the interface expected by UniversalSyncBridge
 */
export const azureCloudClient = {
  /**
   * Push a document to the cloud
   * @param {string} collection - Cloud collection name (e.g., 'sr.crew.v1')
   * @param {string} docId - Document ID
   * @param {Object} data - Document data
   * @returns {Promise<{success: boolean, timestamp?: number, error?: string, offline?: boolean}>}
   */
  push: async (collection, docId, data) => {
    if (!azureApiClient.isAuthenticated()) {
      devError('[SYNC] Not authenticated - JWT token missing. Sync blocked.');
      devError('[SYNC] Auth debug:', {
        tokenExists: !!localStorage.getItem('sms_pro_auth_token'),
        userExists: !!localStorage.getItem('sms_pro_user'),
        timestamp: new Date().toISOString()
      });
      return { success: false, offline: true };
    }

    try {
      // Extract vesselId from data or use current vessel
      const vesselId = data?.vesselId || data?.vessel_id || getCurrentVesselId();

      devLog(`[SYNC] Pushing ${collection}/${docId} to Azure`, data);
      devLog(`[SYNC] Organization: ${getUserOrganizationId()}, Vessel: ${vesselId}`);

      const result = await azureApiClient.push(collection, docId, data, vesselId);

      devLog(`[SYNC] ✅ Successfully pushed ${collection}/${docId} to Azure`);
      return { success: true, timestamp: result.timestamp || Date.now() };
    } catch (error) {
      devLog(`[SYNC] ❌ Failed to push ${collection}/${docId} to Azure:`, error);
      // FT-577: pass the HTTP status through (undefined for network errors).
      return {
        success: false,
        error: error.message,
        ...(typeof error?.status === 'number' ? { status: error.status } : {}),
      };
    }
  },

  /**
   * Pull documents from the cloud
   * @param {string} collection - Cloud collection name
   * @param {string} vesselId - Vessel ID to filter by
   * @param {number} since - Timestamp to get updates since (0 for all)
   * @returns {Promise<Array>} Array of document data
   */
  pull: async (collection, vesselId, since = 0) => {
    if (!azureApiClient.isAuthenticated()) {
      devError('[SYNC] Not authenticated - pull blocked.');
      devError('[SYNC] Auth debug:', {
        tokenExists: !!localStorage.getItem('sms_pro_auth_token'),
        userExists: !!localStorage.getItem('sms_pro_user'),
        operation: 'pull',
        timestamp: new Date().toISOString()
      });
      // 🔴 FT-49: Return error signal, not empty array (2026-03-23)
      // Callers MUST check for .error before hydrating
      return { error: true, status: 'not_authenticated', data: [] };
    }

    try {
      const effectiveVesselId = vesselId || getCurrentVesselId();

      devLog(`[SYNC] Pulling ${collection} from Azure for vessel ${effectiveVesselId}`);

      const response = await azureApiClient.pull(collection, effectiveVesselId, since);

      // Extract records array from response object { success, records, ... }
      const documents = response.records || [];

      devLog(`[SYNC] Pulled ${documents.length} docs from ${collection}`);
      return documents;
    } catch (error) {
      // 🔴 FT-49: Return error signal, not empty array (2026-03-23)
      // This prevents 403/network errors from being silently converted to empty data
      // which would then overwrite localStorage and destroy user data
      const status = error?.response?.status || error?.status || 'unknown';
      devLog(`[SYNC] Pull failed (status: ${status}):`, error);
      return { error: true, status, message: error?.message || 'Pull failed', data: [] };
    }
  },

  /**
   * Subscribe to real-time updates for a collection
   * NOTE: Azure PostgreSQL API does not support real-time subscriptions
   * This is intentional - Supabase real-time was unreliable
   * Instead, use explicit pull() calls or polling
   *
   * @param {string} collection - Cloud collection name
   * @param {string} vesselId - Vessel ID to filter by
   * @param {Function} callback - Called with updated documents
   * @returns {Function} Unsubscribe function (no-op)
   */
  subscribe: (collection, vesselId, callback) => {
    // Return a no-op unsubscribe function
    return () => {
      // No-op: Azure API doesn't support real-time subscriptions
    };
  },

  /**
   * Delete a document from the cloud
   * NOTE: Azure API does not currently expose delete endpoint
   * Documents should be soft-deleted by setting deletedAt timestamp
   *
   * @param {string} collection - Cloud collection name
   * @param {string} docId - Document ID to delete
   * @param {string} vesselId - Vessel ID
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  delete: async (collection, docId, vesselId) => {
    if (!azureApiClient.isAuthenticated()) {
      devError('[SYNC] Not authenticated - delete blocked.');
      devError('[SYNC] Auth debug:', {
        tokenExists: !!localStorage.getItem('sms_pro_auth_token'),
        userExists: !!localStorage.getItem('sms_pro_user'),
        operation: 'delete',
        timestamp: new Date().toISOString()
      });
      return { success: false, offline: true };
    }

    try {
      devLog(`[SYNC] Soft-deleting ${collection}/${docId}`);

      // Soft delete: Push with deletedAt timestamp
      const deleteData = {
        id: docId,
        deletedAt: new Date().toISOString(),
        vesselId: vesselId || getCurrentVesselId()
      };

      const result = await azureApiClient.push(collection, docId, deleteData, vesselId);

      devLog(`[SYNC] Soft-deleted ${collection}:${docId}`);
      return { success: true };
    } catch (error) {
      devLog('[SYNC] Delete failed:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Batch push multiple documents
   * NOTE: Azure API does not currently have batch endpoint
   * Fall back to sequential push operations
   *
   * @param {Array<{collection: string, docId: string, data: Object}>} items
   * @returns {Promise<{success: boolean, pushed: number, failed: number}>}
   */
  batchPush: async (items) => {
    if (!azureApiClient.isAuthenticated() || !items.length) {
      return { success: false, pushed: 0, failed: items.length };
    }

    devLog(`[SYNC] Batch pushing ${items.length} documents (sequential)`);

    let pushed = 0;
    let failed = 0;

    for (const item of items) {
      try {
        const vesselId = item.data?.vesselId || item.data?.vessel_id || getCurrentVesselId();
        await azureApiClient.push(item.collection, item.docId, item.data, vesselId);
        pushed++;
      } catch (error) {
        devLog(`[SYNC] Batch push failed for ${item.collection}/${item.docId}:`, error);
        failed++;
      }
    }

    devLog(`[SYNC] Batch push complete: ${pushed} succeeded, ${failed} failed`);
    return { success: failed === 0, pushed, failed };
  }
};

/**
 * Check if Azure API is configured and available
 * @returns {Promise<boolean>}
 */
export const isAzureApiAvailable = async () => {
  try {
    const healthResponse = await fetch(
      'https://sms-pro-api.wonderfulflower-3a068fa9.uksouth.azurecontainerapps.io/health'
    );
    const data = await healthResponse.json();
    return data.status === 'healthy';
  } catch {
    return false;
  }
};

export default azureCloudClient;
